import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { ArrowRight, CheckCircle, Users, Shield, Star, Package } from "lucide-react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

const STEP_ICONS = [
  <Users className="w-7 h-7" />,
  <Shield className="w-7 h-7" />,
  <Star className="w-7 h-7" />,
  <CheckCircle className="w-7 h-7" />,
];

export default function HowItWorks() {
  const { t } = useTranslation();
  const [stepsRef, stepsVisible] = useScrollAnimation();
  const [includedRef, includedVisible] = useScrollAnimation();

  const steps = t("howitworks_page.detailedSteps", { returnObjects: true }) || [];
  const allIncludes = t("treatments_page.allIncludesItems", { returnObjects: true }) || [];

  return (
    <div className="pt-20">
      <Helmet>
        <title>How Medical Tourism Works | 4 Easy Steps to Treatment in Turkey | Monimek</title>
        <meta name="description" content="From free online consultation to VIP airport transfer and medical procedure. Learn the 4 easy steps to safely get medical treatment in Turkey with Monimek." />
        <link rel="canonical" href="https://www.monizmek.com/how-it-works" />
      </Helmet>
      {/* Hero */}
      <section className="page-hero text-white">
        <div className="max-w-[1200px] mx-auto px-6 py-12 text-center">
          <span className="overline-text text-[#3FA7D6]">{t("howitworks_page.hero.overline")}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white font-outfit tracking-tight mt-3 mb-5" data-testid="hiw-hero-title">
            {t("howitworks_page.hero.title")}
          </h1>
          <p className="text-white/75 text-lg max-w-2xl mx-auto">{t("howitworks_page.hero.subtitle")}</p>
        </div>
      </section>

      {/* Detailed Steps */}
      <section className="py-20 bg-[#F8FAFC]" ref={stepsRef}>
        <div className="max-w-[900px] mx-auto px-6">
          <div className="space-y-8">
            {steps.map((step, i) => (
              <div
                key={i}
                className={`anim-init stagger-${i + 1} ${stepsVisible ? "in-view" : ""} bg-white rounded-3xl p-8 md:p-10 border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.04)] treatment-card`}
                data-testid={`hiw-step-${i + 1}`}
              >
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-2xl why-icon flex items-center justify-center text-white">
                      {STEP_ICONS[i]}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-5xl font-black text-gray-100 font-outfit leading-none">{step.step}</span>
                      <h3 className="text-xl font-bold text-[#0B3C5D] font-outfit">{step.title}</h3>
                    </div>
                    <p className="text-[#6B7280] leading-relaxed mb-5">{step.desc}</p>
                    <div className="flex flex-wrap gap-2">
                      {(step.details || []).map((d, di) => (
                        <span
                          key={di}
                          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#3FA7D6]/10 text-[#3FA7D6] rounded-full font-medium"
                        >
                          <CheckCircle className="w-3 h-3" /> {d}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Everything Included */}
      <section className="py-20 bg-white" ref={includedRef}>
        <div className="max-w-[1200px] mx-auto px-6">
          <div className={`text-center mb-12 anim-init ${includedVisible ? "in-view" : ""}`}>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0B3C5D] font-outfit tracking-tight" data-testid="included-section-title">
              {t("howitworks_page.included")}
            </h2>
          </div>
          <div className={`anim-init stagger-1 ${includedVisible ? "in-view" : ""} bg-[#F8FAFC] rounded-3xl p-8 md:p-12`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {allIncludes.map((inc, i) => (
                <div key={i} className="flex items-center gap-3 bg-white rounded-2xl p-4 border border-gray-100" data-testid={`included-item-${i}`}>
                  <div className="w-8 h-8 rounded-xl bg-[#3FA7D6]/15 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-[#3FA7D6]" />
                  </div>
                  <span className="text-sm font-medium text-[#0B3C5D]">{inc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ready CTA */}
      <section className="cta-gradient py-16">
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white font-outfit mb-4">{t("howitworks_page.readyToStart")}</h2>
          <p className="text-white/75 mb-8">{t("ctaBanner.subtitle")}</p>
          <Link to="/contact" data-testid="hiw-cta" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0B3C5D] font-bold rounded-full hover:scale-105 transition-all duration-200">
            {t("ctaBanner.button")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
